/* =======================================================
   ✅ OGATU — HORIZONTAL STORY STRIP (ASSET JS) — FULL REPLACEMENT
   File: assets/section-ogatu-horizontal-story-strip.js

   Desktop:
   - Sticky viewport driver (pins until travel completes)
   - Scroll maps to translateX (rAF)
   - Spacer height set inline (bulletproof pinning)
   - Recalc on load + resize + section events
   - Start delay (entry buffer) + release buffer
   - Inertial smoothing
   - Optional subtle vertical drift (default OFF)

   Mobile:
   - No JS behavior (native swipe via CSS)
   ======================================================= */

(function () {
  const SELECTOR = '[data-ogatu-storystrip]';
  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

  function isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Bigger multiplier = slower horizontal movement.
  function getSpeedFactor(intensity) {
    switch ((intensity || 'med').toLowerCase()) {
      case 'low':  return 1.35;
      case 'high': return 0.80;
      default:     return 1.00;
    }
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  class OgatuStoryStrip {
    constructor(root) {
      this.root = root;
      this.driver = root.querySelector('.ogatu-storystrip__driver');
      this.viewport = root.querySelector('[data-viewport]');
      this.track = root.querySelector('[data-track]');
      this.spacer = root.querySelector('.ogatu-storystrip__spacer');

      this.enabledSetting = root.dataset.scrollDrive === 'true';
      this.intensity = root.dataset.intensity || 'med';
      this.startDelay = parseInt(root.dataset.startDelay || '120', 10) || 0;

      // TUNING
      this.entryBuffer = this.startDelay;
      this.releaseBuffer = 180;
      this.globalSlow = 1.45;
      this.smoothing = 0.10;
      this.verticalDrift = 0; // set 12–28 if you ever want a tiny drift

      this.raf = null;
      this.animRaf = null;
      this.active = false;

      this.targetX = 0;
      this.targetY = 0;
      this.currentX = 0;
      this.currentY = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onLoadRecalc = this.onLoadRecalc.bind(this);
      this.animate = this.animate.bind(this);

      this.mount();
    }

    mount() {
      if (!this.enabledSetting || prefersReducedMotion() || !isDesktop()) {
        this.teardown();
        return;
      }
      if (!this.viewport || !this.track || !this.spacer || !this.driver) return;

      this.active = true;

      this.recalc();

      // ✅ If there is no travel, don’t pin/drive (prevents “dead sticky”)
      if (this.travel <= 1) {
        this.teardown();
        return;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      window.addEventListener('load', this.onLoadRecalc, { once: true });

      setTimeout(() => this.recalc(), 120);
      this.onScroll();
    }

    teardown() {
      this.active = false;

      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('load', this.onLoadRecalc);

      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;

      if (this.animRaf) cancelAnimationFrame(this.animRaf);
      this.animRaf = null;

      this.targetX = this.currentX = 0;
      this.targetY = this.currentY = 0;

      if (this.track) this.track.style.transform = 'translate3d(0,0,0)';
      if (this.spacer) this.spacer.style.height = '1px';
      if (this.driver) this.driver.style.minHeight = '';
    }

    onLoadRecalc() {
      if (!this.active) return;
      this.recalc();
      this.onScroll();
    }

    recalc() {
      if (!this.active) return;

      this.intensity = this.root.dataset.intensity || 'med';
      this.startDelay = parseInt(this.root.dataset.startDelay || '120', 10) || 0;
      this.entryBuffer = this.startDelay;

      const vw = this.viewport.clientWidth;
      const trackW = this.track.scrollWidth;

      this.travel = Math.max(0, trackW - vw);

      const mult = getSpeedFactor(this.intensity) * this.globalSlow;

      this.scrollLen = Math.max(
        1,
        Math.round((this.travel * mult) + this.entryBuffer + this.releaseBuffer)
      );

      this.spacer.style.height = `${this.scrollLen}px`;
      this.driver.style.minHeight = `calc(100vh + ${this.scrollLen}px)`;

      this.rootTop = this.root.getBoundingClientRect().top + window.scrollY;
      this._lastTopSync = window.scrollY;

      this.currentX = this.targetX;
      this.currentY = this.targetY;
      this.applyTransform(this.currentX, this.currentY);
    }

    onResize() {
      if (!this.enabledSetting || prefersReducedMotion()) return;

      if (!isDesktop()) {
        this.teardown();
        return;
      }

      if (!this.active) {
        this.mount();
        return;
      }

      this.recalc();

      // if travel becomes 0 after resize, teardown
      if (this.travel <= 1) {
        this.teardown();
        return;
      }

      this.onScroll();
    }

    applyTransform(x, y) {
      this.track.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`;
    }

    animate() {
      if (!this.active) return;

      const dx = this.targetX - this.currentX;
      const dy = this.targetY - this.currentY;

      this.currentX += dx * this.smoothing;
      this.currentY += dy * this.smoothing;

      this.applyTransform(this.currentX, this.currentY);

      if (Math.abs(dx) < 0.25 && Math.abs(dy) < 0.25) {
        this.currentX = this.targetX;
        this.currentY = this.targetY;
        this.applyTransform(this.currentX, this.currentY);
        this.animRaf = null;
        return;
      }

      this.animRaf = requestAnimationFrame(this.animate);
    }

    setTargets(progress01) {
      this.targetX = -this.travel * progress01;

      if (this.verticalDrift > 0) {
        const hump = Math.sin(Math.PI * progress01);
        this.targetY = -this.verticalDrift * hump;
      } else {
        this.targetY = 0;
      }

      if (!this.animRaf) this.animRaf = requestAnimationFrame(this.animate);
    }

    onScroll() {
      if (!this.active) return;
      if (this.raf) return;

      this.raf = requestAnimationFrame(() => {
        this.raf = null;

        const y = window.scrollY;

        if (!this._lastTopSync || (y - this._lastTopSync) > 800) {
          this.rootTop = this.root.getBoundingClientRect().top + window.scrollY;
          this._lastTopSync = y;
        }

        const local = y - this.rootTop;
        const effective = Math.max(0, local - this.entryBuffer);
        const denom = Math.max(1, this.scrollLen - this.entryBuffer - this.releaseBuffer);

        let progress = clamp(effective / denom, 0, 1);
        progress = easeInOutCubic(progress);

        this.setTargets(progress);
      });
    }
  }

  function mountAll() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.__ogatuStoryStrip) return;
      root.__ogatuStoryStrip = new OgatuStoryStrip(root);
    });
  }

  function remountOnSectionEvents(e) {
    const root = e.target?.querySelector?.(SELECTOR);
    if (!root) return;

    if (root.__ogatuStoryStrip) {
      root.__ogatuStoryStrip.teardown();
      delete root.__ogatuStoryStrip;
    }
    root.__ogatuStoryStrip = new OgatuStoryStrip(root);
  }

  document.addEventListener('DOMContentLoaded', mountAll);
  document.addEventListener('shopify:section:load', remountOnSectionEvents);
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target?.querySelector?.(SELECTOR);
    if (root?.__ogatuStoryStrip) {
      root.__ogatuStoryStrip.teardown();
      delete root.__ogatuStoryStrip;
    }
  });
})();